import {Button} from '@dropbox/dig-components/buttons';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {type WorkstreamAuditCompanyGroup, type WorkstreamAuditRow} from 'client';
import {t} from 'i18next';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useDefaultWorkstreamTimeframe, useWorkstreamAudit} from 'views/workstreams/detail/hooks';
import {WorkstreamAuditCompanyReadinessHeader} from 'views/workstreams/detail/WorkstreamAuditCompanyReadinessHeader';
import {WorkstreamAuditTable} from 'views/workstreams/detail/WorkstreamAuditTable';
import {WORKSTREAM_AUDIT_COMPANY_TAB} from 'views/workstreams/detail/WorkstreamAuditTabs';
import {
  getWorkstreamAuditCompanyReadinessCategory,
  getWorkstreamAuditCompanyReadinessRows,
  WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER,
  type WorkstreamAuditCompanyReadinessCounts,
  type WorkstreamAuditCompanyReadinessFilter,
} from 'views/workstreams/detail/workstreamAuditUtils';

export type AuditScope = 'dri' | 'exec_sponsor' | 'company';

// A group stays while its parent or any child is shown; the parent is kept as
// the children's header either way, like the readiness filter does.
const filterGroups = (
  groups: WorkstreamAuditCompanyGroup[],
  isShown: (row: WorkstreamAuditRow) => boolean
) =>
  groups.flatMap((group) => {
    const children = (group.subworkstreams ?? []).filter(
      (row): row is WorkstreamAuditRow => Boolean(row) && isShown(row)
    );
    return isShown(group.parent) || children.length > 0
      ? [{...group, subworkstreams: children}]
      : [];
  });

// Readiness counts for the rows in scope. Unscored rows (pillar headers) have
// no category, so they never count.
const countReadiness = (rows: WorkstreamAuditRow[]) =>
  rows.reduce<WorkstreamAuditCompanyReadinessCounts>(
    (acc, row) => {
      const category = getWorkstreamAuditCompanyReadinessCategory(row);
      return category ? {...acc, [category]: acc[category] + 1} : acc;
    },
    {complete: 0, attention: 0, critical: 0}
  );

const totalOf = (counts: WorkstreamAuditCompanyReadinessCounts) =>
  counts.complete + counts.attention + counts.critical;

// The pane and the header's switch share one query (same key, so one fetch).
const useAuditData = () => {
  const onTimeTimeframe = useDefaultWorkstreamTimeframe();
  const query = useWorkstreamAudit({
    // Wait for the timeframes rather than load the audit twice.
    enabled: Boolean(onTimeTimeframe),
    onTimeTimeframe,
  });
  const audit = query.data;
  const derived = useMemo(() => {
    const companyGroups = (audit?.company.groups ?? []).filter((group) => Boolean(group?.parent));
    const companyRows = getWorkstreamAuditCompanyReadinessRows(companyGroups);
    const roleScope = (rows: WorkstreamAuditRow[] | undefined) => {
      const ids = new Set((rows ?? []).map((row) => row.id));
      return {ids, counts: countReadiness(companyRows.filter((row) => ids.has(row.id)))};
    };
    return {
      companyGroups,
      companyCounts: countReadiness(companyRows),
      roles: {
        dri: roleScope(audit?.dri.rows),
        exec_sponsor: roleScope(audit?.exec_sponsor.rows),
      },
    };
  }, [audit]);
  return {...query, ...derived};
};

// The first of DRI, Exec sponsor, Company you have rows in, until you pick
// (`requested` is null until then). A role you have no rows in is disabled.
const useScope = (requested: AuditScope | null) => {
  const data = useAuditData();
  const has = {
    dri: totalOf(data.roles.dri.counts) > 0,
    exec_sponsor: totalOf(data.roles.exec_sponsor.counts) > 0,
    company: true,
  } satisfies Record<AuditScope, boolean>;
  const fallback: AuditScope = has.dri ? 'dri' : has.exec_sponsor ? 'exec_sponsor' : 'company';
  const scope: AuditScope = requested && has[requested] ? requested : fallback;
  return {...data, has, scope};
};

/**
 * /workstreams: Audit's DRI | Exec sponsor | Company switch, in the
 * page header where Details | Metrics sits (they don't apply to Audit).
 */
export const AuditScopeSwitch = ({
  requested,
  onChange,
}: {
  requested: AuditScope | null;
  onChange: (scope: AuditScope) => void;
}) => {
  const {scope, has} = useScope(requested);

  return (
    <SegmentedControl
      selectedValue={scope}
      onSelection={(value) => value && onChange(value as AuditScope)}
    >
      <SegmentedControl.Option variant="text" value="dri" disabled={!has.dri}>
        DRI
      </SegmentedControl.Option>
      <SegmentedControl.Option variant="text" value="exec_sponsor" disabled={!has.exec_sponsor}>
        Exec sponsor
      </SegmentedControl.Option>
      <SegmentedControl.Option variant="text" value="company">
        Company
      </SegmentedControl.Option>
    </SegmentedControl>
  );
};

/**
 * /workstreams: Audit in the pane. One company table instead of
 * /workstreams' per-role tabs: DRI and exec sponsor rows are all company rows,
 * so either role narrows the table to its rows and the readiness tiles count
 * whichever scope is showing. No title; the rail's Audit row already says
 * where you are.
 */
export const AuditPane = ({requested}: {requested: AuditScope | null}) => {
  const hasLoggedViewRef = useRef(false);
  const [readiness, setReadiness] = useState<WorkstreamAuditCompanyReadinessFilter>(
    WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER
  );
  const {
    data: audit,
    isPending,
    isError,
    refetch,
    companyGroups,
    companyCounts,
    roles,
    scope,
  } = useScope(requested);
  // A new scope starts from all of it.
  const [readinessScope, setReadinessScope] = useState(scope);
  if (readinessScope !== scope) {
    setReadinessScope(scope);
    setReadiness(WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER);
  }

  useEffect(() => {
    if (isError || !audit || hasLoggedViewRef.current) return;
    hasLoggedViewRef.current = true;
    analyticsLogger().logEvent('WORKSTREAM_AUDIT_VIEWED', {
      attention_count: audit.attention_count,
      exec_sponsor_count: audit.exec_sponsor.count,
      dri_count: audit.dri.count,
      reporter_count: audit.reporter.count,
      steam_owner_count: audit.steam_owner.count,
      company_count: audit.company.count,
    });
  }, [audit, isError]);

  if (isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingY="44"
        style={{gap: 16}}
      >
        <Text color="subtle">{t('error_loading_workstreams')}</Text>
        <Button variant="outline" size="small" onClick={() => void refetch()}>
          {t('retry')}
        </Button>
      </Box>
    );
  }

  if (isPending || !audit) {
    return (
      <Box display="flex" justifyContent="center" paddingY="44">
        <Spinner size="small" />
      </Box>
    );
  }

  const role = scope === 'company' ? null : roles[scope];
  const counts = role ? role.counts : companyCounts;
  const totalCount = totalOf(counts);
  const visibleCount =
    readiness === WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER ? totalCount : counts[readiness];
  const visibleGroups = filterGroups(
    companyGroups,
    (row) =>
      (!role || role.ids.has(row.id)) &&
      (readiness === WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER ||
        getWorkstreamAuditCompanyReadinessCategory(row) === readiness)
  );

  return (
    <Box display="flex" flexDirection="column" height="100%" style={{minHeight: 0}}>
      <WorkstreamAuditCompanyReadinessHeader
        activeFilter={readiness}
        counts={counts}
        totalCount={totalCount}
        onFilterChange={setReadiness}
        hideTopBorder
      />
      <Box display="flex" padding="16" style={{flex: 1, minHeight: 0}}>
        <WorkstreamAuditTable
          companyGroups={visibleGroups}
          // Overloaded-DRI warnings count across the whole company, whatever's showing.
          driOwnershipCompanyGroups={companyGroups}
          isCompanyMode
          auditTab={WORKSTREAM_AUDIT_COMPANY_TAB}
          showEmptyState={visibleCount === 0}
        />
      </Box>
    </Box>
  );
};
